/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useRef, useEffect } from 'react';
import plantumlEncoder from 'plantuml-encoder';
import config from '../../config';

const ZoomableDiagram = ({ plantumlCode, onCodeUpdate, hideLargeWarning = false, onSplit = null }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCode, setEditedCode] = useState(plantumlCode);
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('Diagram Load Error');
  const [imgSrc, setImgSrc] = useState(null);
  const [isRendering, setIsRendering] = useState(!!plantumlCode);
  const [darkMode, setDarkMode] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const imgFallbackAttemptedRef = useRef(false);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(zoom * delta, 0.5), 5);
    setZoom(newZoom);
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const SCROLL_STEP = 150;
  const scroll = (dx, dy) => setPan(p => ({ x: p.x + dx, y: p.y + dy }));

  const isLargeDiagram = !hideLargeWarning && plantumlCode && (
    plantumlCode.length > 4000 || plantumlCode.split('\n').length > 80
  );

  const downloadPuml = () => {
    const blob = new Blob([plantumlCode || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.puml';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      resetView();
    }
  };

  const handleRetry = () => {
    setImageError(false);
    setErrorMessage('');
    setRetryCount(prev => prev + 1);
  };

  const handleEditCode = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (onCodeUpdate && typeof onCodeUpdate === 'function') {
      onCodeUpdate(editedCode);
    }
    setImageError(false);
    setErrorMessage('');
    setIsEditing(false);
    setRetryCount(prev => prev + 1);
  };

  const handleCancelEdit = () => {
    setEditedCode(plantumlCode);
    setIsEditing(false);
  };

  const cleanPlantUMLCode = (code) => {
    let cleaned = code
      // Normalize Unicode punctuation to ASCII (prevents garbled chars like â€™ in rendered image)
      .replace(/[‘’‚‛]/g, "'")
      .replace(/[“”„‟]/g, '"')
      .replace(/–/g, '-')
      .replace(/—/g, '--')
      .replace(/…/g, '...')
      .replace(/ /g, ' ')
      .replace(/^plantuml\s+/i, '')
      .replace(/^@startuml\s*/i, '')
      .replace(/\s*@enduml\s*$/i, '')
      .trim();

    // `queue` is a valid element in BOTH sequence and component/deployment
    // diagrams and renders fine on current plantuml.com. Do NOT rewrite it to
    // `participant` — that keyword is sequence-diagram-only, so injecting it into
    // a component/architecture diagram (component/package/cloud/rectangle) makes
    // PlantUML throw a syntax error, which plantuml.com returns as an error image
    // with HTTP 400 → the <img> fails to load and nothing renders.

    // Force lifeline color to black so CSS invert(1) renders them as bright white
    cleaned = cleaned.replace(
      /skinparam\s+sequenceLifeLineBorderColor\s+#[0-9a-fA-F]+/gi,
      'skinparam sequenceLifeLineBorderColor #000000'
    );
    if (!/sequenceLifeLineBorderColor/i.test(cleaned)) {
      cleaned = 'skinparam sequenceLifeLineBorderColor #000000\n' + cleaned;
    }

    return cleaned;
  };

  useEffect(() => {
    setImageError(false);
    setEditedCode(plantumlCode);
    setErrorMessage('');
    imgFallbackAttemptedRef.current = false;
  }, [plantumlCode]);

  const handleImageLoad = (e) => {
    setIsRendering(false);
    const img = e.target;
    // A genuine PlantUML syntax error returns HTTP 400 (handled by <img onError>), so
    // a successfully-loaded image here is almost always valid. Only treat it as broken
    // if it is essentially blank — a real diagram, even a single node, is bigger than
    // this. The old 600×100 floor false-flagged small valid diagrams as syntax errors.
    if (img.naturalWidth < 100 || img.naturalHeight < 40) {
      setImageError(true);
      setErrorMessage('PlantUML syntax error detected');
    } else {
      setImageError(false);
      setErrorMessage('');
    }
  };

  /*
   * PlantUML Rendering — 2-tier strategy
   *
   * Tier 1 (primary): POST code to intelog-controller /api/v1/plantuml/render.
   *   Backend forwards the code to a rendering server (self-hosted PlantUML or
   *   kroki.io) via HTTP POST — no URL length limit. Backend returns the PNG as
   *   a base64 data URI: { imageData: "data:image/png;base64,..." }.
   *   The <img src> is set to this data URI — no external browser request needed.
   *
   * Tier 2 (fallback): If the backend POST fails (server down / network error),
   *   plantuml-encoder encodes the code in-browser using DEFLATE and builds a
   *   plantuml.com GET URL. Works for small/medium diagrams. Large or complex
   *   diagrams may hit plantuml.com's URL length limit or OOM errors.
   *
   * Tier 3 (error state): If Tier 2 also fails, an error overlay is shown with
   *   Retry and Edit Code options.
   *
   * Code is cleaned before rendering (cleanPlantUMLCode):
   *   - Strips @startuml / @enduml (re-added in normalised form)
   *   - Keeps `queue` as-is (valid in component diagrams; do not rewrite)
   *   - Forces sequenceLifeLineBorderColor #000000 (CSS invert filter support)
   */
  useEffect(() => {
    const code = editedCode || plantumlCode;
    if (!code) return;
    const cleanedCode = cleanPlantUMLCode(code);
    const normalizedCode = `@startuml\n${cleanedCode}\n@enduml`;

    console.log('[ZoomableDiagram] Rendering started — code length:', normalizedCode.length, 'chars');
    console.log('[ZoomableDiagram] Tier 1: POSTing to backend proxy:', `${config.domain}/api/v1/plantuml/render`);

    setIsRendering(true);
    setImageError(false);
    setErrorMessage('');
    setImgSrc(null);
    imgFallbackAttemptedRef.current = false;

    const controller = new AbortController();

    fetch(`${config.domain}/api/v1/plantuml/render`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': localStorage.getItem('token'),
      },
      body: JSON.stringify({ code: normalizedCode }),
      signal: controller.signal,
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        // Prefer base64 imageData (no URL length limit) over legacy imageUrl
        const src = data.imageData || data.imageUrl;
        if (data.imageData) {
          console.log('[ZoomableDiagram] Tier 1 SUCCESS — base64 data URI, first 120 chars:', data.imageData.slice(0, 120));
        } else {
          console.log('[ZoomableDiagram] Tier 1 SUCCESS — imageUrl:', src);
        }
        setImgSrc(src);
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          console.log('[ZoomableDiagram] Render aborted (component unmounted or code changed)');
          return;
        }
        console.warn('[ZoomableDiagram] Tier 1 FAILED:', err.message);
        console.log('[ZoomableDiagram] Tier 2: Falling back to plantuml-encoder (in-browser DEFLATE)');
        try {
          const encoded = plantumlEncoder.encode(normalizedCode);
          const fallbackUrl = `https://www.plantuml.com/plantuml/png/${encoded}`;
          console.log('[ZoomableDiagram] Tier 2 SUCCESS — encoded length:', encoded.length, '| url:', fallbackUrl);
          // Keep isRendering=true — spinner stays until <img> onLoad/onError fires
          setImgSrc(fallbackUrl);
        } catch (encodeErr) {
          console.error('[ZoomableDiagram] Tier 2 FAILED — encoding error:', encodeErr.message);
          console.error('[ZoomableDiagram] Tier 3: All rendering strategies exhausted — showing error state');
          setImageError(true);
          setErrorMessage('Failed to render diagram');
          setIsRendering(false);
        }
      });

    return () => controller.abort();
  // editedCode intentionally excluded — retryCount (incremented on save) re-triggers when needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantumlCode, retryCount]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [zoom]);

  if (!plantumlCode) {
    return (
      <div style={{
        backgroundColor: '#0d1117',
        border: '1px solid #30363d',
        borderRadius: 8,
        padding: 48,
        textAlign: 'center',
        color: '#8b949e',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <p style={{ color: '#8b949e' }}>No PlantUML code provided</p>
        </div>
      </div>
    );
  }

  // DARK EDIT MODE
  if (isEditing) {
    return (
      <div style={{
        backgroundColor: '#0d1117',
        border: '1px solid #30363d',
        borderRadius: 8,
        padding: 24,
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #30363d'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: '#e6edf3'
          }}>
            ✏️ Edit PlantUML Code
          </h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleSaveEdit}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1a6fff',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1558cc'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#1a6fff'}
            >
              ✓ Save & Regenerate
            </button>
            <button
              onClick={handleCancelEdit}
              style={{
                padding: '10px 20px',
                backgroundColor: '#161b22',
                color: '#8b949e',
                border: '1px solid #30363d',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
        <textarea
          value={editedCode}
          onChange={(e) => setEditedCode(e.target.value)}
          style={{
            width: '100%',
            minHeight: '350px',
            padding: 16,
            fontFamily: '"Fira Code", "Courier New", monospace',
            fontSize: 14,
            lineHeight: '1.6',
            border: '1px solid #30363d',
            borderRadius: 4,
            resize: 'vertical',
            backgroundColor: '#161b22',
            color: '#e6edf3',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#1a6fff'}
          onBlur={(e) => e.target.style.borderColor = '#30363d'}
          placeholder="Enter PlantUML code here..."
        />
        <div style={{
          marginTop: 12,
          padding: 14,
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 4,
          fontSize: 13,
          color: '#8b949e'
        }}>
          <strong style={{ color: '#e6edf3' }}>💡 Tips:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, lineHeight: '1.8' }}>
            <li>Code should start with <code style={{
              backgroundColor: '#21262d',
              color: '#ff7b72',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>@startuml</code> and end with <code style={{
              backgroundColor: '#21262d',
              color: '#ff7b72',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>@enduml</code></li>
            <li>Use <code style={{
              backgroundColor: '#21262d',
              color: '#79c0ff',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>-&gt;</code> for arrows between participants</li>
            <li>Wrap participant names with spaces in quotes</li>
            <li>Check for unclosed <code style={{
              backgroundColor: '#21262d',
              color: '#79c0ff',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>alt</code>/<code style={{
              backgroundColor: '#21262d',
              color: '#79c0ff',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>else</code>/<code style={{
              backgroundColor: '#21262d',
              color: '#79c0ff',
              padding: '2px 6px',
              borderRadius: 3,
              fontFamily: 'monospace',
              border: '1px solid #30363d'
            }}>end</code> blocks</li>
          </ul>
        </div>
      </div>
    );
  }

  const containerStyle = {
    position: isFullscreen ? 'fixed' : 'relative',
    top: isFullscreen ? 0 : 'auto',
    left: isFullscreen ? 0 : 'auto',
    width: isFullscreen ? '100vw' : '100%',
    height: isFullscreen ? '100vh' : '500px',
    // backgroundColor: '#0d1117',  /* dark mode */
    backgroundColor: darkMode ? '#0d1117' : '#ffffff',
    // border: isFullscreen ? 'none' : '1px solid #30363d',  /* dark mode */
    border: isFullscreen ? 'none' : (darkMode ? '1px solid #30363d' : '1px solid #d0d7de'),
    borderRadius: isFullscreen ? 0 : 8,
    overflow: 'hidden',
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isFullscreen ? 9999 : 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const imageStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: 'center center',
    maxWidth: 'none',
    maxHeight: 'none',
    // filter: 'invert(1) hue-rotate(180deg) brightness(1.6) contrast(1.1)',  /* dark mode */
    filter: darkMode ? 'invert(1) hue-rotate(180deg) brightness(1.6) contrast(1.1)' : 'drop-shadow(0 1px 4px rgba(0,0,0,0.12))',
    padding: 20,
    userSelect: 'none',
    pointerEvents: 'none',
    display: imageError ? 'none' : 'block',
  };

  const controlsStyle = {
    position: 'absolute',
    top: 16,
    right: 16,
    display: 'flex',
    gap: 10,
    zIndex: 10
  };

  const buttonStyle = {
    padding: '10px 14px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    transition: 'all 0.2s',
    color: '#e6edf3'
  };

  const zoomInfoStyle = {
    position: 'absolute',
    bottom: 16,
    left: 16,
    backgroundColor: 'rgba(0,0,0,0.65)',
    color: '#fff',
    padding: '8px 14px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 10
  };

  // DARK ERROR OVERLAY
  const errorOverlayStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#161b22',
    padding: 40,
    borderRadius: 8,
    border: '1px solid #30363d',
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
    textAlign: 'center',
    maxWidth: '500px',
    width: '90%',
    zIndex: 20
  };

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div
        ref={containerRef}
        style={containerStyle}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div style={controlsStyle}>
          <button
            style={buttonStyle}
            onClick={() => setZoom(zoom * 1.2)}
            title="Zoom In"
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            🔍+
          </button>
          <button
            style={buttonStyle}
            onClick={() => setZoom(zoom * 0.8)}
            title="Zoom Out"
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            🔍-
          </button>
          <button
            style={buttonStyle}
            onClick={resetView}
            title="Reset View"
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            🎯
          </button>
          <button
            style={buttonStyle}
            onClick={() => setDarkMode(d => !d)}
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button
            style={buttonStyle}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            {isFullscreen ? '🗗' : '🗖'}
          </button>
        </div>

        {/* ✅ BLACK & WHITE ERROR OVERLAY */}
        {imageError && (
          <div style={errorOverlayStyle}>
            <div style={{
              fontSize: 64,
              marginBottom: 20
            }}>
              ⚠️
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: 22,
              fontWeight: 700,
              color: '#e6edf3'
            }}>
              {errorTitle}
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: 15,
              color: '#8b949e',
              lineHeight: '1.6'
            }}>
              The diagram could not be generated due to syntax errors in the PlantUML code.
              {errorMessage && (
                <>
                  <br />
                  <span style={{ color: '#e6edf3', fontWeight: 500 }}>{errorMessage}</span>
                </>
              )}
            </p>

            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              marginBottom: 24
            }}>
              <button
                onClick={handleRetry}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#161b22',
                  color: '#e6edf3',
                  border: '1px solid #30363d',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 15,
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.target.style.backgroundColor = '#21262d'; }}
                onMouseOut={(e) => { e.target.style.backgroundColor = '#161b22'; }}
              >
                🔄 Retry
              </button>
              <button
                onClick={handleEditCode}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1a6fff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 15,
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.target.style.backgroundColor = '#1558cc'; }}
                onMouseOut={(e) => { e.target.style.backgroundColor = '#1a6fff'; }}
              >
                ✏️ Edit Code
              </button>
            </div>

            <div style={{
              padding: 16,
              backgroundColor: '#0d1117',
              border: '1px solid #30363d',
              borderRadius: 4,
              fontSize: 13,
              color: '#8b949e',
              textAlign: 'left'
            }}>
              <strong style={{
                display: 'block',
                marginBottom: 10,
                color: '#e6edf3',
                fontSize: 14
              }}>
                🔍 Common Issues:
              </strong>
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: '1.8', color: '#8b949e' }}>
                <li>Word <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#ff7b72',
                  fontWeight: 600
                }}>plantuml</code> before <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>@startuml</code></li>
                <li>Missing <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>@startuml</code> or <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>@enduml</code> tags</li>
                <li>Invalid arrow syntax (use <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>-&gt;</code> or <code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>--&gt;</code>)</li>
                <li>Unclosed blocks (<code style={{
                  backgroundColor: '#21262d',
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                  color: '#79c0ff'
                }}>alt/else/end</code>)</li>
                <li>Missing quotes around participant names with spaces</li>
              </ul>
            </div>
          </div>
        )}

        {isRendering && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 15,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            // background: '#0d1117',  /* dark mode */
            background: darkMode ? '#0d1117' : '#ffffff', gap: 16,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.08)',
              borderTopColor: '#1a6fff',
              animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ color: '#8b949e', fontSize: 13, fontWeight: 500, letterSpacing: 0.3 }}>
              Rendering diagram…
            </span>
          </div>
        )}

        {imgSrc && <img
          ref={imageRef}
          src={imgSrc}
          alt="Generated PlantUML Diagram"
          style={{ ...imageStyle, display: !imageError ? 'block' : 'none' }}
          draggable={false}
          onError={() => {
            // First failure — try Tier 2 in-browser encoding before showing error
            if (!imgFallbackAttemptedRef.current) {
              imgFallbackAttemptedRef.current = true;
              try {
                const code = editedCode || plantumlCode;
                const cleaned = cleanPlantUMLCode(code);
                const normalized = `@startuml\n${cleaned}\n@enduml`;
                const encoded = plantumlEncoder.encode(normalized);
                console.log('[ZoomableDiagram] img onError — Tier 2 fallback (in-browser encode), length:', encoded.length);
                setImgSrc(`https://www.plantuml.com/plantuml/png/${encoded}`);
                return; // keep spinner up; onLoad/onError will fire again
              } catch (e) {
                console.error('[ZoomableDiagram] Tier 2 fallback failed:', e.message);
              }
            }
            // Both tiers exhausted
            setIsRendering(false);
            setImageError(true);
            setErrorTitle('Diagram Load Error');
            setErrorMessage('Could not load diagram — plantuml.com may be temporarily unavailable. Try Retry.');
          }}
          onLoad={handleImageLoad}
        />}

        {!imageError && (
          <div style={zoomInfoStyle}>
            Zoom: {Math.round(zoom * 100)}% | Scroll to zoom, drag to pan
          </div>
        )}

        {isFullscreen && (
          <button
            onClick={toggleFullscreen}
            style={{
              position: 'absolute',
              top: 20,
              left: 20,
              padding: '12px 20px',
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 600,
              zIndex: 11,
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#161b22'}
          >
            ✕ Close Fullscreen
          </button>
        )}

        {/* Directional pan buttons */}
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 36px)',
            gridTemplateRows: 'repeat(3, 36px)',
            gap: 3,
            zIndex: 10,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Row 1: Up */}
          <div />
          <button
            title="Pan Up"
            onClick={(e) => { e.stopPropagation(); scroll(0, SCROLL_STEP); }}
            style={{ ...buttonStyle, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161b22'}
          >▲</button>
          <div />
          {/* Row 2: Left / Center / Right */}
          <button
            title="Pan Left"
            onClick={(e) => { e.stopPropagation(); scroll(SCROLL_STEP, 0); }}
            style={{ ...buttonStyle, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161b22'}
          >◀</button>
          <button
            title="Reset Pan"
            onClick={(e) => { e.stopPropagation(); setPan({ x: 0, y: 0 }); }}
            style={{ ...buttonStyle, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161b22'}
          >⊙</button>
          <button
            title="Pan Right"
            onClick={(e) => { e.stopPropagation(); scroll(-SCROLL_STEP, 0); }}
            style={{ ...buttonStyle, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161b22'}
          >▶</button>
          {/* Row 3: Down */}
          <div />
          <button
            title="Pan Down"
            onClick={(e) => { e.stopPropagation(); scroll(0, -SCROLL_STEP); }}
            style={{ ...buttonStyle, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#21262d'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161b22'}
          >▼</button>
          <div />
        </div>
      </div>

      <div style={{
        marginTop: 12,
        fontSize: 13,
        color: '#8b949e',
        textAlign: 'center',
        fontWeight: 500
      }}>
        💡 Scroll to zoom, drag to pan, click fullscreen for better viewing
      </div>

      {isLargeDiagram && (
        <div style={{
          marginTop: 10,
          padding: '10px 16px',
          backgroundColor: 'rgba(255, 171, 0, 0.08)',
          border: '1px solid rgba(255, 171, 0, 0.35)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#f0b429', lineHeight: 1.5 }}>
            ⚠️ This diagram is large — the rendered image may be incomplete or broken.
            Download the <code style={{ fontFamily: 'monospace', fontSize: 12 }}>.puml</code> file and open it in a PlantUML tool (VS Code extension, IntelliJ, or <a href="https://www.plantuml.com/plantuml/uml/" target="_blank" rel="noopener noreferrer" style={{ color: '#f0b429' }}>plantuml.com</a>) to view it correctly.
          </span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            {onSplit && (
              <button
                onClick={onSplit}
                style={{
                  padding: '6px 14px',
                  backgroundColor: 'rgba(46, 191, 165, 0.15)',
                  border: '1px solid rgba(46, 191, 165, 0.5)',
                  borderRadius: 4,
                  color: '#2ebfa5',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(46,191,165,0.25)'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'rgba(46,191,165,0.15)'}
              >
                🧩 Split by flow
              </button>
            )}
            <button
              onClick={downloadPuml}
              style={{
                padding: '6px 14px',
                backgroundColor: 'rgba(255, 171, 0, 0.15)',
                border: '1px solid rgba(255, 171, 0, 0.5)',
                borderRadius: 4,
                color: '#f0b429',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(255,171,0,0.25)'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'rgba(255,171,0,0.15)'}
            >
              ⬇ Download .puml
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ZoomableDiagram;